/**
 * Dashboard charts — inline SVG, no chart library, faithful to the legacy panel
 * (js/dashboard.js: renderAllUsageChart / renderCumulativeChart / renderBarChart).
 *
 * Colours, smoothing tension and the two distinct WoW formulas are copied from
 * legacy so the React Overview reads identically. Hover behaviour (crosshair +
 * marker dots + tooltip) mirrors legacy's onAllChartHover / onCumChartHover /
 * onBarChartHover — the tooltip is a portalled HTML box (theme-aware) instead of
 * legacy's in-SVG text, but shows the same values.
 */

import { useState, type ReactNode, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Parse 'YYYY-MM-DD' without Date() to avoid TZ off-by-one. */
export function fmtDate(s: string): string {
  const [, m, d] = s.split('-');
  return `${+d} ${MON[+m - 1]}`;
}
/** Full 'DD Mon YYYY' for tooltips (matches legacy's en-GB long form). */
export function fmtDateFull(s: string): string {
  const [y, m, d] = s.split('-');
  return `${d} ${MON[+m - 1]} ${y}`;
}
/** Evenly spaced label indices: all when short, ~7 otherwise. */
export function tickIdxs(n: number): number[] {
  if (n <= 8) return Array.from({ length: n }, (_, i) => i);
  const step = (n - 1) / 6;
  return Array.from({ length: 7 }, (_, i) => Math.round(i * step));
}
/** Smoothed cubic path with horizontal control handles (legacy dx = Δx·0.4). */
export function smooth(pts: [number, number][]): string {
  if (!pts.length) return '';
  if (pts.length === 1) return `M${pts[0][0]},${pts[0][1]}`;
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const dx = (x1 - x0) * 0.4;
    d += ` C${x0 + dx},${y0} ${x1 - dx},${y1} ${x1},${y1}`;
  }
  return d;
}

const USAGE_COLORS = ['#3B82F6', '#EC4899', '#A78BFA', '#F0C666', '#86DA9A',
  '#EF4444', '#60A5FA', '#2DD4BF', '#FB923C', '#818CF8'];

export type WindowKey = '7' | '30' | 'all';
export const WINDOWS: { key: WindowKey; label: string }[] = [
  { key: '7', label: '7d' }, { key: '30', label: '30d' }, { key: 'all', label: 'All' },
];

/** WoW for the cumulative legend: latest total vs the cumulative value 7 days prior. */
export function wowCumulative(cum: number[]): { pct: string; dir: 'up' | 'down' | 'flat' } {
  if (cum.length < 2) return { pct: '0.0', dir: 'flat' };
  const total = cum[cum.length - 1];
  const prev = cum[Math.max(0, cum.length - 8)];
  const diff = total - prev;
  const pct = prev > 0 ? (diff / prev * 100).toFixed(1) : (diff > 0 ? '100.0' : '0.0');
  return { pct, dir: diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat' };
}
/** WoW for sparklines: sum of last 7 daily values vs the prior 7. */
export function wowSum(vals: number[]): { text: string; dir: 'up' | 'down' | 'flat' | 'new' } {
  const sum = (a: number[]) => a.reduce((t, v) => t + v, 0);
  const thisS = sum(vals.slice(-7));
  const prevS = sum(vals.slice(-14, -7));
  if (prevS === 0) return thisS > 0 ? { text: 'new', dir: 'new' } : { text: '', dir: 'flat' };
  const pct = Math.round((thisS - prevS) / prevS * 100);
  return { text: `${Math.abs(pct)}% WoW`, dir: pct >= 0 ? 'up' : 'down' };
}

function Wow({ dir, children }: { dir: string; children: ReactNode }) {
  if (!children) return null;
  const arrow = dir === 'up' || dir === 'new' ? '▲' : dir === 'down' ? '▼' : '';
  const cls = dir === 'up' || dir === 'new' ? 'wow-up' : dir === 'down' ? 'wow-down' : 'wow-flat';
  return <span className={`wow ${cls}`}>{arrow && <span>{arrow} </span>}{children}</span>;
}

/* ── Hover plumbing ──────────────────────────────────────────────────────────
 * Nearest-index hit test against the SVG viewBox, plus a cursor-following
 * tooltip portalled to <body> (so it never clips inside a card's overflow).   */

interface Hover { i: number; cx: number; cy: number; }

/** Map a mouse event to the nearest data index using the SVG's on-screen box. */
function hitIndex(e: MouseEvent<SVGSVGElement>, W: number, n: number, xAt: (i: number) => number): number {
  const rect = e.currentTarget.getBoundingClientRect();
  if (!rect.width) return 0;
  const mx = ((e.clientX - rect.left) / rect.width) * W;
  let idx = 0, min = Infinity;
  for (let i = 0; i < n; i++) {
    const d = Math.abs(xAt(i) - mx);
    if (d < min) { min = d; idx = i; }
  }
  return idx;
}

function ChartTooltip({ hover, children }: { hover: Hover; children: ReactNode }) {
  if (typeof document === 'undefined') return null;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const left = Math.min(hover.cx + 16, vw - 272);
  const top = Math.max(hover.cy - 20, 8);
  return createPortal(
    <div className="chart-tooltip" style={{ left, top }}>{children}</div>,
    document.body,
  );
}

/* ── Credits Usage: stacked bars per day + smoothed daily-total overlay ──────── */

export interface UsageDay { date: string; credits: number; byAccount: Record<string, number>; }

export function UsageChart({ usage }: { usage: UsageDay[] }) {
  const [hover, setHover] = useState<Hover | null>(null);
  if (!usage.length) return <p className="muted">No usage in this window.</p>;
  const W = 820, H = 200, padL = 10, padR = 10, padT = 18, padB = 34;
  const chartW = W - padL - padR, chartH = H - padT - padB, baseline = padT + chartH;
  const n = usage.length;
  const maxVal = Math.max(...usage.map((d) => d.credits), 1);
  const band = chartW / n;
  const barW = Math.min(band * 0.8, band - 6);

  // Stable colour per account across the whole window.
  const emails = Array.from(new Set(usage.flatMap((d) => Object.keys(d.byAccount)))).sort();
  const colorOf = (e: string) => USAGE_COLORS[emails.indexOf(e) % USAGE_COLORS.length];

  const xAt = (i: number) => padL + band * i + band / 2;
  const linePts = usage.map((d, i) =>
    [xAt(i), padT + (1 - d.credits / maxVal) * chartH] as [number, number]);
  const ticks = tickIdxs(n);
  const hi = hover && hover.i < n ? hover.i : null;
  const hd = hi != null ? usage[hi] : null;

  return (
    <>
      <svg className="chart-svg chart-interactive" viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label="Credits usage per day"
        onMouseMove={(e) => setHover({ i: hitIndex(e, W, n, xAt), cx: e.clientX, cy: e.clientY })}
        onMouseLeave={() => setHover(null)}>
        {usage.map((d, i) => {
          const x = padL + band * i + (band - barW) / 2;
          let off = 0;
          return (
            <g key={d.date}>
              {emails.map((e) => {
                const v = d.byAccount[e] || 0;
                if (v <= 0) return null;
                const h = v / maxVal * chartH;
                const y = baseline - off - h;
                off += h;
                return <rect key={e} x={x} y={y} width={barW} height={h} rx={1.5}
                  fill={colorOf(e)} opacity={0.85} />;
              })}
            </g>
          );
        })}
        <path d={smooth(linePts)} fill="none" stroke="#FFFFFF" strokeWidth={1.5}
          strokeDasharray="4,3" opacity={0.4} />
        {ticks.map((i) => (
          <text key={i} x={padL + band * i + band / 2} y={H - 12} fontSize={11}
            textAnchor="middle" fill="var(--color-text-tertiary)">{fmtDate(usage[i].date)}</text>
        ))}
        {hi != null && (
          <g pointerEvents="none">
            <line x1={xAt(hi)} x2={xAt(hi)} y1={padT} y2={baseline} strokeWidth={1}
              stroke="var(--color-text-tertiary)" strokeDasharray="4,3" opacity={0.7} />
            <circle cx={linePts[hi][0]} cy={linePts[hi][1]} r={4}
              fill="var(--color-surface-default)" stroke="var(--color-text-primary)" strokeWidth={2} />
          </g>
        )}
      </svg>
      <div className="chart-legend">
        {emails.map((e) => (
          <span key={e} className="legend-item">
            <span className="legend-sw" style={{ background: colorOf(e) }} />
            {e.split('@')[0]}
          </span>
        ))}
      </div>
      {hover && hd && (
        <ChartTooltip hover={hover}>
          <div className="tt-date">{fmtDateFull(hd.date)}</div>
          <div className="tt-total">{hd.credits.toLocaleString()} credits</div>
          {Object.entries(hd.byAccount).sort((a, b) => b[1] - a[1]).map(([email, c]) => (
            <div key={email} className="tt-row">
              <span className="tt-sw" style={{ background: colorOf(email) }} />
              <span className="tt-name">{email.split('@')[0]}</span>
              <span className="tt-val">{c.toLocaleString()}</span>
            </div>
          ))}
        </ChartTooltip>
      )}
    </>
  );
}

/* ── Growth (Cumulative): 3 self-scaled cumulative series ────────────────────── */

export interface GrowthDay { date: string; registrations: number; sites: number; credits: number; }
const GROWTH_SERIES = [
  { key: 'registrations', label: 'Users', color: '#3B82F6' },
  { key: 'sites', label: 'Sites', color: '#86DA9A' },
  { key: 'credits', label: 'Credits', color: '#A78BFA' },
] as const;

export function GrowthChart({ data }: { data: GrowthDay[] }) {
  const [hover, setHover] = useState<Hover | null>(null);
  if (!data.length) return <p className="muted">No growth data in this window.</p>;
  const W = 860, H = 200, padL = 10, padR = 10, padT = 12, padB = 28;
  const chartW = W - padL - padR, chartH = H - padT - padB, baseline = padT + chartH;
  const n = data.length;
  const xAt = (i: number) => n === 1 ? padL + chartW / 2 : padL + (chartW * i) / (n - 1);

  const series = GROWTH_SERIES.map((s) => {
    let c = 0;
    const cum = data.map((d) => (c += d[s.key]));
    const max = Math.max(...cum, 1);
    const pts = cum.map((v, i) => [xAt(i), baseline - (v / max) * chartH] as [number, number]);
    return { ...s, cum, total: cum[cum.length - 1], pts, wow: wowCumulative(cum) };
  });
  const credits = series[2];
  const ticks = tickIdxs(n);
  const hi = hover && hover.i < n ? hover.i : null;

  return (
    <>
      <div className="chart-head chart-head-legend">
        <div className="growth-legend">
          {series.map((s) => (
            <span key={s.key} className="legend-item">
              <span className="legend-dot" style={{ background: s.color }} />
              {s.label}: <b>{s.total.toLocaleString()}</b>&nbsp;
              <Wow dir={s.wow.dir}>{s.wow.dir !== 'flat' ? `${s.wow.pct}% WoW` : ''}</Wow>
            </span>
          ))}
        </div>
      </div>
      <svg className="chart-svg chart-interactive" viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label="Cumulative growth"
        onMouseMove={(e) => setHover({ i: hitIndex(e, W, n, xAt), cx: e.clientX, cy: e.clientY })}
        onMouseLeave={() => setHover(null)}>
        <path d={`${smooth(credits.pts)} L${xAt(n - 1)},${baseline} L${xAt(0)},${baseline} Z`}
          fill={credits.color} opacity={0.08} />
        {series.map((s) => (
          <path key={s.key} d={smooth(s.pts)} fill="none" stroke={s.color} strokeWidth={2} />
        ))}
        <circle cx={credits.pts[n - 1][0]} cy={credits.pts[n - 1][1]} r={4}
          fill="#fff" stroke={credits.color} strokeWidth={2} />
        {ticks.map((i) => (
          <text key={i} x={xAt(i)} y={H - 8} fontSize={11} textAnchor="middle"
            fill="var(--color-text-tertiary)">{fmtDate(data[i].date)}</text>
        ))}
        {hi != null && (
          <g pointerEvents="none">
            <line x1={xAt(hi)} x2={xAt(hi)} y1={padT} y2={baseline} strokeWidth={1}
              stroke="var(--color-text-tertiary)" strokeDasharray="4,3" opacity={0.7} />
            {series.map((s) => (
              <circle key={s.key} cx={s.pts[hi][0]} cy={s.pts[hi][1]} r={4}
                fill="var(--color-surface-default)" stroke={s.color} strokeWidth={2} />
            ))}
          </g>
        )}
      </svg>
      {hover && hi != null && (
        <ChartTooltip hover={hover}>
          <div className="tt-date">{fmtDateFull(data[hi].date)}</div>
          {series.map((s) => (
            <div key={s.key} className="tt-row">
              <span className="tt-sw" style={{ background: s.color }} />
              <span className="tt-name">{s.label}</span>
              <span className="tt-val">{s.cum[hi].toLocaleString()}</span>
            </div>
          ))}
        </ChartTooltip>
      )}
    </>
  );
}

/* ── Sparkline cards: daily bars + smoothed overlay ──────────────────────────── */

export function SparkCard({ title, data, field, color, total }:
  { title: string; data: GrowthDay[]; field: 'registrations' | 'sites' | 'credits'; color: string; total: number }) {
  const [hover, setHover] = useState<Hover | null>(null);
  const vals = data.map((d) => d[field]);
  const wow = wowSum(vals);
  const W = 420, H = 160, padL = 6, padR = 6, padT = 8, padB = 22;
  const chartW = W - padL - padR, chartH = H - padT - padB, baseline = padT + chartH;
  const n = data.length || 1;
  const maxVal = Math.max(...vals, 1);
  const band = chartW / n;
  const barW = Math.max(1, Math.min(band * 0.7, band - 2));
  const xAt = (i: number) => padL + band * i + band / 2;
  const linePts = vals.map((v, i) =>
    [xAt(i), padT + (1 - v / maxVal) * chartH] as [number, number]);
  const ticks = data.length ? tickIdxs(data.length) : [];
  const hi = hover && hover.i < data.length ? hover.i : null;
  const unit = field === 'credits' ? ' credits' : field === 'sites' ? ' sites' : ' users';

  return (
    <div className="card spark-card">
      <div className="spark-head">
        <h3 className="spark-title">{title}</h3>
        <span className="spark-total">{total.toLocaleString()} total{' '}
          <Wow dir={wow.dir}>{wow.text}</Wow></span>
      </div>
      {data.length === 0 ? <p className="muted small">No data.</p> : (
        <svg className="chart-svg chart-interactive" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title}
          onMouseMove={(e) => setHover({ i: hitIndex(e, W, data.length, xAt), cx: e.clientX, cy: e.clientY })}
          onMouseLeave={() => setHover(null)}>
          {vals.map((v, i) => {
            const h = v / maxVal * chartH;
            return <rect key={i} x={padL + band * i + (band - barW) / 2} y={baseline - h}
              width={barW} height={h} rx={2} fill={color} opacity={0.75} />;
          })}
          <path d={smooth(linePts)} fill="none" stroke={color} strokeWidth={1.5} opacity={0.9} />
          {ticks.map((i) => (
            <text key={i} x={padL + band * i + band / 2} y={H - 6} fontSize={10} textAnchor="middle"
              fill="var(--color-text-tertiary)">{fmtDate(data[i].date)}</text>
          ))}
          {hi != null && (
            <g pointerEvents="none">
              <line x1={xAt(hi)} x2={xAt(hi)} y1={padT} y2={baseline} strokeWidth={1}
                stroke="var(--color-text-tertiary)" strokeDasharray="4,3" opacity={0.7} />
              <circle cx={linePts[hi][0]} cy={linePts[hi][1]} r={3.5}
                fill="var(--color-surface-default)" stroke={color} strokeWidth={2} />
            </g>
          )}
        </svg>
      )}
      {hover && hi != null && (
        <ChartTooltip hover={hover}>
          <div className="tt-total">{vals[hi].toLocaleString()}{unit}</div>
          <div className="tt-date">{fmtDateFull(data[hi].date)}</div>
        </ChartTooltip>
      )}
    </div>
  );
}
